import type { Period } from '@nav/shared';
import { useEffect, useMemo, useState } from 'react';

import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/states';
import { AnimatedContent } from '@/components/react-bits/AnimatedContent';
import { usePublishDataFreshness } from '@/components/layout/DataFreshnessContext';
import type { NavWindow } from '@/lib/api';
import { DailyNavTable } from '@/features/funds/components/DailyNavTable';
import { GrowthComboChart } from '@/features/funds/components/GrowthComboChart';
import { NavMonthlyTable } from '@/features/funds/components/NavMonthlyTable';
import { NavPerformanceCard } from '@/features/funds/components/NavPerformanceCard';
import { PortfolioHoldings } from '@/features/funds/components/PortfolioHoldings';
import { SflInsightGrid } from '@/features/funds/components/SflInsightGrid';
import { SflKpiRow } from '@/features/funds/components/SflKpiRow';
import { SflMacroStrip } from '@/features/funds/components/SflMacroStrip';
import { SflMasthead } from '@/features/funds/components/SflMasthead';
import { SflProductTabs } from '@/features/funds/components/SflProductTabs';
import { SflSectionTitle } from '@/features/funds/components/SflSectionTitle';
import { computeDataInsights } from '@/features/funds/insightsEngine';
import {
  applyLiveMacros,
  marketContextFor,
  sflDisclaimer,
  toInsightItem,
} from '@/features/funds/marketContext';
import { BRAND } from '@/features/funds/palette';
import {
  useFundSummaryQuery,
  useFundsQuery,
  useHoldingsQuery,
  useInsightsQuery,
  useMarketSnapshotQuery,
  useNavDailyQuery,
  useNavMonthlyAllQuery,
  useNavMonthlyQuery,
} from '@/features/funds/queries';
import {
  DEFAULT_COMBO_METRICS,
  type ComboFilter,
  type ComboMetric,
  type SflFundCode,
  sflProduct,
} from '@/features/funds/sflProducts';

/**
 * Public Siji Fixed Link page.
 *
 * Two product tabs (SFL IDR / SFL Dollar). SFIS is hidden. KPI, NAV trend,
 * combo chart, and the data table are live. The combo chart's product filter
 * (IDR/Dollar/Both) and metric filter (MoM/3 Bulan/YoY, multi-select) are
 * independent controls — any combination of metrics can be shown together.
 * The macro strip is editorial
 * market context, except the Rupiah/Fed Funds Rate/US Treasury 10Y cards,
 * which are overlaid with a free live quote when the server can reach one
 * (`applyLiveMacros`) — see `marketDataService.ts` on the server for sourcing.
 * The "Insight & Analisa" section is a featured row of two Riset AI cards
 * (macro left, fund right — written by Claude, cited and analyst-approved,
 * `/api/funds/:code/insights`), then a one-line-per-fact strip of the
 * rule-based cards computed live from the fund's own NAV series
 * (`insightsEngine.ts`), then any hand-written editorial cards. Each tier
 * keeps its own badge, visually distinct on purpose — see `SflInsightGrid`.
 * The disclaimer below the grid describes whichever kinds are actually
 * present, so it stays true whether or not the AI feature is switched on.
 * Holdings and daily NAV stay below as operational sections.
 */
export function FundsPage() {
  const fundsQuery = useFundsQuery();

  const [fundCode, setFundCode] = useState<SflFundCode>('SFFR');
  const [timeframe, setTimeframe] = useState<NavWindow>('all');
  const [holdingsPeriod, setHoldingsPeriod] = useState<Period | null>(null);
  const [comboFilter, setComboFilter] = useState<ComboFilter>('SFFR');
  const [comboMetrics, setComboMetrics] = useState<ComboMetric[]>([...DEFAULT_COMBO_METRICS]);

  const allFunds = useMemo(() => fundsQuery.data?.funds ?? [], [fundsQuery.data]);
  const fund = useMemo(() => allFunds.find((f) => f.code === fundCode) ?? null, [allFunds, fundCode]);
  const currency = sflProduct(fundCode)?.currency ?? fund?.currency ?? 'IDR';
  const context = marketContextFor(fundCode);
  const product = sflProduct(fundCode);

  useEffect(() => {
    setHoldingsPeriod(null);
    setTimeframe('all');
  }, [fundCode]);

  useEffect(() => {
    setComboFilter((prev) => (prev === 'both' ? 'both' : fundCode));
  }, [fundCode]);

  const summaryQuery = useFundSummaryQuery(fundCode);
  const windowedQuery = useNavMonthlyQuery(fundCode, timeframe);
  const idrAllQuery = useNavMonthlyAllQuery('SFFR');
  const usdAllQuery = useNavMonthlyAllQuery('SFFD');
  const selectedAllQuery = fundCode === 'SFFD' ? usdAllQuery : idrAllQuery;
  const dailyQuery = useNavDailyQuery(fundCode, 30);
  const holdingsQuery = useHoldingsQuery(fundCode, holdingsPeriod);
  const marketSnapshotQuery = useMarketSnapshotQuery();
  const insightsQuery = useInsightsQuery(fundCode);

  usePublishDataFreshness(summaryQuery.data?.dataFreshness);

  const handleProductChange = (code: SflFundCode) => {
    setFundCode(code);
  };

  if (fundsQuery.isError) {
    return (
      <div className="min-h-full" style={{ background: BRAND.pageBg }}>
        <div className="dash-wrap px-4 py-6">
          <Card>
            <ErrorState error={fundsQuery.error} onRetry={() => void fundsQuery.refetch()} />
          </Card>
        </div>
      </div>
    );
  }

  const fundsLoading = fundsQuery.isPending;
  const kpiLoading = fundsLoading || selectedAllQuery.isPending;
  const windowedLoading = fundsLoading || windowedQuery.isPending;
  const comboLoading = idrAllQuery.isPending || usdAllQuery.isPending;

  const liveMacros = useMemo(
    () => applyLiveMacros(fundCode, context.macros, marketSnapshotQuery.data),
    [fundCode, context.macros, marketSnapshotQuery.data],
  );

  const dataInsights = useMemo(
    () => (product ? computeDataInsights(selectedAllQuery.data?.points ?? [], product) : []),
    [product, selectedAllQuery.data],
  );
  /**
   * Section order is deliberate. The grid receives the raw published Riset AI
   * cards and picks the newest macro and fund card as a featured pair (the
   * macro card explains WHY the numbers moved, the fund card WHAT they did);
   * the computed Data Live items follow as a compact facts strip because they
   * restate figures the KPI row and the fund card already give; editorial cards,
   * if any, come last. `insightItems` is the flat list of everything shown, and
   * exists only so the disclaimer can describe what is actually on the page.
   *
   * A failed or empty insights query contributes nothing and is not an error —
   * the computed facts render either way.
   */
  const publishedInsights = useMemo(() => insightsQuery.data?.insights ?? [], [insightsQuery.data]);
  const otherItems = useMemo(() => [...context.insights, ...dataInsights], [context.insights, dataInsights]);
  const insightItems = useMemo(
    () => [...publishedInsights.map(toInsightItem), ...otherItems],
    [publishedInsights, otherItems],
  );

  return (
    <div className="min-h-full" style={{ background: BRAND.pageBg }}>
      <div className="dash-wrap px-4 pt-8 pb-12">
        <AnimatedContent>
          <SflMasthead dataFreshness={summaryQuery.data?.dataFreshness} />
        </AnimatedContent>

        <AnimatedContent delay={0.05} className="mt-8">
          <SflProductTabs selected={fundCode} onSelect={handleProductChange} />
        </AnimatedContent>

        <AnimatedContent delay={0.08} className="mt-9">
          <SflKpiRow
            points={selectedAllQuery.data?.points ?? []}
            currency={currency}
            isLoading={kpiLoading}
            error={selectedAllQuery.error}
            onRetry={() => void selectedAllQuery.refetch()}
          />
        </AnimatedContent>

        <AnimatedContent delay={0.1} className="mt-9">
          <SflMacroStrip items={liveMacros} asOfLabel={context.asOfLabel} />
        </AnimatedContent>

        <AnimatedContent className="mt-10">
          <SflSectionTitle>Tren NAV Akhir Bulan</SflSectionTitle>
          <NavPerformanceCard
            points={windowedQuery.data?.points ?? []}
            currency={currency}
            timeframe={timeframe}
            onTimeframeChange={setTimeframe}
            isLoading={windowedLoading}
            error={windowedQuery.error}
            onRetry={() => void windowedQuery.refetch()}
          />
        </AnimatedContent>

        <AnimatedContent className="mt-10">
          <SflSectionTitle>Pertumbuhan (MoM / 3 Bulan / YoY)</SflSectionTitle>
          <GrowthComboChart
            idrPoints={idrAllQuery.data?.points ?? []}
            usdPoints={usdAllQuery.data?.points ?? []}
            filter={comboFilter}
            onFilterChange={setComboFilter}
            metrics={comboMetrics}
            onMetricsChange={setComboMetrics}
            isLoading={comboLoading}
            error={idrAllQuery.error ?? usdAllQuery.error}
            onRetry={() => {
              void idrAllQuery.refetch();
              void usdAllQuery.refetch();
            }}
          />
        </AnimatedContent>

        <AnimatedContent className="mt-10">
          <SflSectionTitle>Insight & Analisa</SflSectionTitle>
          <SflInsightGrid researched={publishedInsights} items={otherItems} />
        </AnimatedContent>

        <AnimatedContent className="mt-10">
          <SflSectionTitle>Data Lengkap</SflSectionTitle>
          <NavMonthlyTable
            points={windowedQuery.data?.points ?? []}
            currency={currency}
            sourceNote={context.sourceNote}
            isLoading={windowedLoading}
            error={windowedQuery.error}
            onRetry={() => void windowedQuery.refetch()}
          />
        </AnimatedContent>

        <AnimatedContent className="mt-10">
          <SflSectionTitle>Portfolio Holdings</SflSectionTitle>
          <PortfolioHoldings
            fund={fund}
            data={holdingsQuery.data}
            selectedPeriod={holdingsPeriod}
            onSelectPeriod={setHoldingsPeriod}
            isLoading={fundsLoading || holdingsQuery.isPending}
            error={holdingsQuery.error}
            onRetry={() => void holdingsQuery.refetch()}
          />
        </AnimatedContent>

        <AnimatedContent className="mt-10">
          <SflSectionTitle>NAV Harian</SflSectionTitle>
          <DailyNavTable
            fund={fund}
            points={dailyQuery.data?.points ?? []}
            isLoading={fundsLoading || dailyQuery.isPending}
            error={dailyQuery.error}
            onRetry={() => void dailyQuery.refetch()}
          />
        </AnimatedContent>

        <div
          className="mt-10 px-5 py-4"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 11.5,
            color: BRAND.textFaint,
            background: 'rgba(27,36,54,0.035)',
            border: `1px solid ${BRAND.panelBorder}`,
            borderRadius: 8,
            lineHeight: 1.6,
          }}
        >
          <b style={{ color: BRAND.ink, fontWeight: 600 }}>Catatan:</b>{' '}
          {sflDisclaimer(insightItems)}
        </div>

        <footer
          className="mt-5 pt-5 text-center"
          style={{
            borderTop: `1px solid ${BRAND.panelBorder}`,
            fontFamily: 'var(--font-sans)',
            fontSize: 11.5,
            color: BRAND.textFaint,
          }}
        >
          Siji Fixed Link — Dashboard disusun dari data NAV bulanan internal & kondisi pasar
          publik terkini
        </footer>
      </div>
    </div>
  );
}

export default FundsPage;
